import { DashboardContent } from 'src/layouts/dashboard';
import { useNotify, useTranslate } from 'react-admin';
import { PermissionGuard } from 'src/routes/components/permission-guard';
import ErrorPage403 from 'src/pages/error/403';
import { Grid, Box, Button, Card, CardContent, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography, IconButton, Paper, InputAdornment } from '@mui/material';
import { useEffect, useState, KeyboardEvent } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import useConfig from '../configs/useConfigs';
import useReward from './useReward';
import { resetReward, saveRewardLeaderboard, saveRewardReferral, saveRewardTicket, updateEarlyBird, updateConfigs, retrieveReferralPool } from 'src/pages/admin/services/whitelistService';

export interface Tier {
  position?: number;
  volumeUsdc: number;
  tickets: number;
}

export interface RewardTier {
  position?: number;
  rankFrom: number;
  rankTo: number;
  amountUsdc: string;
}

export interface Referral {
  position?: number;
  minValid: number;
  amountUsdc: number;
}

const handlePositiveInteger = (
  e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  setter: React.Dispatch<React.SetStateAction<any>>,
  allowZero: boolean = false
) => {
  const rawValue = e.target.value;
  const cleanValue = rawValue.replace(/[^0-9]/g, '');
  if (cleanValue === '') {
    setter('');
    return;
  }
  let num = parseInt(cleanValue, 10);
  if (isNaN(num)) {
    setter('');
    return;
  }
  if (!allowZero && num < 1) {
    num = 1;
  }
  if (allowZero && num < 0) {
    num = 0;
  }
  setter(num);
};

const onBlurPositiveInteger = (
  value: any,
  setter: React.Dispatch<React.SetStateAction<any>>,
  defaultValue: number = 1
) => {
  if (
    value === '' ||
    value === undefined ||
    value === null ||
    (typeof value === 'number' && value < 1)
  ) {
    setter(defaultValue);
  }
};

const preventInvalidKeys = (e: KeyboardEvent<HTMLDivElement>) => {
  if (e.key === '.' || e.key === '-' || e.key === 'e' || e.key === 'E') {
    e.preventDefault();
  }
};

const View = () => {
  const translate = useTranslate();
  const notify = useNotify();

  const { retrieve: retrieveConfigs, configsData } = useConfig();
  const { retrieveTickets, tickets, retrieveLeaderboard, leaderboard, retrieveReferral, referrals } = useReward();

  const [capacity, setCapacity] = useState<number | ''>('');
  const [minTradeVolume, setMinTradeVolume] = useState<number | ''>('');
  const [perUserReward, setPerUserReward] = useState<number | ''>('');
  const [totalRewardPool, setTotalRewardPool] = useState<number | ''>('');

  const [unlockThreshold, setUnlockThreshold] = useState<number | ''>('');
  // const [initialSlot, setInitialSlot] = useState<number | ''>('');

  const [tiers, setTiers] = useState<Tier[]>([]);
  const [nextId, setNextId] = useState<number>(5);

  const [rewardTiers, setRewardTiers] = useState<RewardTier[]>([]);
  const [nextRewardId, setNextRewardId] = useState<number>(0);
  const [rewardTierErrors, setRewardTierErrors] = useState<Record<number, { rankFrom?: string; rankTo?: string }>>({});

  const [referralTiers, setReferralTiers] = useState<Referral[]>([]);
  const [nextReferralId, setNextReferral] = useState<number>(0);

  const [referralPool, setReferralPool] = useState<{ totalUsdc: string; remainingUsdc: string } | null>(null);

  useEffect(() => {
    retrieveConfigs();
    retrieveTickets();
    retrieveLeaderboard();
    retrieveReferral();
    retrieveReferralPool()
      .then((res) => {
        const data = res.data?.data ?? res.data;
        setReferralPool({ totalUsdc: data?.totalUsdc, remainingUsdc: data?.remainingUsdc });
      })
      .catch((err) => console.log('referral pool error:', err));
  }, [retrieveConfigs, retrieveTickets, retrieveLeaderboard, retrieveReferral]);

  useEffect(() => {
    if (configsData) {
      setCapacity(configsData.earlyBirdTopN || '');
      setMinTradeVolume(configsData.earlyBirdMinVolumeUsdc || '');
      setPerUserReward(configsData.earlyBirdPerUserUsdc || '');
      setTotalRewardPool(configsData.earlyBirdPoolTotalUsdc ? Number(configsData.earlyBirdPoolTotalUsdc) : '');
    
      setUnlockThreshold(configsData.unlockVolumeUsdc ? Number(configsData.unlockVolumeUsdc) : '');
      // setInitialSlot(configsData.marketingShowcaseQuota || '');
    }
  }, [configsData]);

  useEffect(() => {
    if (tickets) {
      setTiers(tickets.map((t, i) => ({ ...t, position: i })));
      setNextId(tickets.length);
    }
  }, [tickets]);

  useEffect(() => {
    if (leaderboard) {
      setRewardTiers(leaderboard.map((t, i) => ({ ...t, position: i, amountUsdc: String(t.amountUsdc) })));
      setNextRewardId(leaderboard.length);
    }
  }, [leaderboard]);

  useEffect(() => {
    if (referrals) {
      setReferralTiers(referrals.map((t, i) => ({ ...t, position: i })));
      setNextReferral(referrals.length);
    }
  }, [referrals]);

  const updateTier = (id: number, field: keyof Tier, value: number) => {
    setTiers((prev) =>
      prev.map((tier) => (tier.position === id ? { ...tier, [field]: value } : tier))
    );
  };

  const handleAddPredictTier = () => {
    setTiers((prev) => [...prev, { position: nextId, volumeUsdc: 1, tickets: 1 }]);
    setNextId((prev) => prev + 1);
  };

  const handleDeleteTier = (id: number) => {
    setTiers((prev) => prev.filter((tier) => tier.position !== id));
  };

  const handleSavePredictTicketTier = async () => {
    if(tiers.length === 0) return
    try {
      const response = await saveRewardTicket(tiers.map(({ position, ...rest }) => rest));
      if (response.status === 200) {
        notify(translate('whitelist.success'), { type: 'success', autoHideDuration: 1000 });
      } else {
        notify(translate('whitelist.failed'), { type: 'error', autoHideDuration: 1000 });
      }
    } catch (error: any) {
      console.log('error: ', error);
      notify(error?.response?.data?.message || translate('whitelist.failed'), { type: 'error', autoHideDuration: 5000 });
    }
  };

  const handleEarlyBirdSave = async () => {
    try {
      const response = await updateEarlyBird(
        String(totalRewardPool),
        String(perUserReward),
        String(minTradeVolume),
        String(capacity),
      );
      if (response.status === 200) {
        retrieveConfigs();
        notify(translate('whitelist.success'), { type: 'success', autoHideDuration: 1000 });
      } else {
        notify(translate('whitelist.failed'), { type: 'error', autoHideDuration: 1000 });
      }
    } catch (error: any) {
      console.log(error);
      notify(error?.response?.data?.message || translate('whitelist.failed'), { type: 'error', autoHideDuration: 5000 });
    }
  };

  const handleWhiteListSave = async () => {
    try {
      const response = await updateConfigs({unlockVolumeUsdc: Number(unlockThreshold) });
      if (response.status === 200) {
        retrieveConfigs();
        notify(translate('whitelist.success'), { type: 'success', autoHideDuration: 1000 });
      } else {
        notify(translate('whitelist.failed'), { type: 'error', autoHideDuration: 1000 });
      }
    } catch (error: any) {
      console.log(error);
      notify(error?.response?.data?.message || translate('whitelist.failed'), { type: 'error', autoHideDuration: 5000 });
    }
  };

  // --- Leaderboard reward handlers ---
  const updateRewardTier = (id: number, field: keyof RewardTier, value: number | string) => {
    setRewardTiers((prev) =>
      prev.map((tier) => (tier.position === id ? { ...tier, [field]: value } : tier))
    );
    setRewardTierErrors({});
  };

  const handleAddRewardTier = () => {
    setRewardTiers((prev) => [
      ...prev,
      { position: nextRewardId, rankFrom: 1, rankTo: 1, amountUsdc: '1' },
    ]);
    setNextRewardId((prev) => prev + 1);
    setRewardTierErrors({});
  };

  const handleDeleteRewardTier = (id: number) => {
    setRewardTiers((prev) => prev.filter((tier) => tier.position !== id));
    setRewardTierErrors({});
  };

  const validateRewardTiers = (): boolean => {
    const errors: Record<number, { rankFrom?: string; rankTo?: string }> = {};
    for (let i = 0; i < rewardTiers.length; i += 1) {
      const tier = rewardTiers[i];
      const pos = tier.position!;
      if (tier.rankTo < tier.rankFrom) {
        errors[pos] = { ...errors[pos], rankTo: 'Rank To must be greater than or equal to Rank From' };
      }
      if (i > 0 && tier.rankFrom <= rewardTiers[i - 1].rankFrom) {
        errors[pos] = { ...errors[pos], rankFrom: `Rank From must be greater than previous Rank To (${rewardTiers[i - 1].rankTo})` };
      }
    }
    setRewardTierErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveRewardTier = async () => {
    if (rewardTiers.length === 0) return;
    if (!validateRewardTiers()) return;
    try {
      const response = await saveRewardLeaderboard(
        rewardTiers.map(({ position, ...rest }) => rest)
      );
      if (response.status === 200) {
        retrieveLeaderboard();
        notify(translate('whitelist.success'), { type: 'success', autoHideDuration: 1000 });
      } else {
        notify(translate('whitelist.failed'), { type: 'error', autoHideDuration: 1000 });
      }
    } catch (error: any) {
      console.log('error: ', error);
      notify(error?.response?.data?.message || translate('whitelist.failed'), { type: 'error', autoHideDuration: 5000 });
    }
  };

  const updateInviteTier = (id: number, field: keyof Referral, value: number) => {
    setReferralTiers((prev) =>
      prev.map((tier) => (tier.position === id ? { ...tier, [field]: value } : tier))
    );
  };

  const handleDeleteInviteTier = (id: number) => {
    setReferralTiers((prev) => prev.filter((tier) => tier.position !== id));
  };

  const handleAddInviteTier = () => {
    setReferralTiers((prev) => [...prev, { position: nextReferralId, minValid: 1, amountUsdc: 1 }]);
    setNextReferral((prev) => prev + 1);
  };

  const handleSaveInviteTier = async () => {
    if(referralTiers.length === 0) return
    try {
      const response = await saveRewardReferral(referralTiers.map(({ position, ...rest }) => rest));
      
      if (response.status === 200) {
        retrieveReferral();
        notify(translate('whitelist.success'), { type: 'success', autoHideDuration: 1000 });
      } else {
        notify(translate('whitelist.failed'), { type: 'error', autoHideDuration: 1000 });
      }
    } catch (error: any) {
      console.log('error: ', error);
      notify(error?.response?.data?.message || translate('whitelist.failed'), { type: 'error', autoHideDuration: 5000 });
    }
  };

  const handleResetToDefault = async () => {
    try {
      const response = await resetReward();
      if (response.status === 200) {
        notify(translate('whitelist.success'), { type: 'success', autoHideDuration: 1000 });
        retrieveConfigs();
        retrieveTickets();
        retrieveLeaderboard();
        retrieveReferral();
      } else {
        notify(translate('whitelist.failed'), { type: 'error', autoHideDuration: 1000 });
      }
    } catch (error: any) {
      console.log('error: ', error);
    }
  };

  return (
    <DashboardContent>
      <PermissionGuard resource="whitelist-reward-config" action="read" fallback={<ErrorPage403 />}>
      <Card sx={{ mt: 3, pb: 2 }}>
        <CardContent>
          <Typography variant="h5" component="h2" gutterBottom fontWeight="medium" sx={{ mb: 3 }}>
            {translate('whitelist.reward.title')}
          </Typography>

          {/* ========== EARLY BIRD SECTION ========== */}
          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
            <Typography variant="h6" component="h2" gutterBottom fontWeight="medium">
              {translate('whitelist.earlyBird.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {translate('whitelist.reward.earlyBirdRewardDescprition')}
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label={translate('whitelist.reward.quotaLimit')}
                  type="number"
                  value={capacity}
                  onChange={(e) => handlePositiveInteger(e, setCapacity)}
                  onBlur={() => onBlurPositiveInteger(capacity, setCapacity, 1)}
                  onKeyDown={preventInvalidKeys}
                  inputProps={{ min: 1, step: 1 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {translate('whitelist.personUnit')}
                      </InputAdornment>
                    ),
                  }}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label={translate('whitelist.reward.thresholdTradingVolume')}
                  type="number"
                  value={minTradeVolume}
                  onChange={(e) => handlePositiveInteger(e, setMinTradeVolume, true)} // allow zero
                  onBlur={() => onBlurPositiveInteger(minTradeVolume, setMinTradeVolume, 0)}
                  onKeyDown={preventInvalidKeys}
                  inputProps={{ min: 0, step: 1 }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">USDT</InputAdornment>,
                  }}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label={translate('whitelist.reward.singleReward')}
                  type="number"
                  value={perUserReward}
                  onChange={(e) => handlePositiveInteger(e, setPerUserReward)}
                  onBlur={() => onBlurPositiveInteger(perUserReward, setPerUserReward, 1)}
                  onKeyDown={preventInvalidKeys}
                  inputProps={{ min: 1, step: 1 }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">USDT</InputAdornment>,
                  }}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label={translate('whitelist.reward.totalReward')}
                  type="number"
                  value={totalRewardPool}
                  onChange={(e) => handlePositiveInteger(e, setTotalRewardPool, true)}
                  onBlur={() => onBlurPositiveInteger(totalRewardPool, setTotalRewardPool, 0)}
                  onKeyDown={preventInvalidKeys}
                  inputProps={{ min: 0, step: 1 }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">USDT</InputAdornment>,
                  }}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>

            <PermissionGuard resource="whitelist-reward-config" action="update" fallback={null}>
              <Box sx={{ display: 'flex', gap: 2, mt: 4, mb: 3 }}>
                <Button variant="contained" onClick={handleEarlyBirdSave}>
                  {translate('whitelist.save')}
                </Button>
              </Box>
            </PermissionGuard>
          </Paper>

          {/* ========== WHITELIST ELIGIBILITY SECTION ========== */}
          <Paper variant="outlined" sx={{ p: 2, mt: 3, bgcolor: 'background.default' }}>
            <Typography variant="h6" component="h2" gutterBottom fontWeight="medium">
              {translate('whitelist.reward.whitelistEligibility')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {translate('whitelist.reward.whitelistEligibilityDescription')}
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={translate('whitelist.reward.unlockThreshold')}
                  type="number"
                  value={unlockThreshold}
                  onChange={(e) => handlePositiveInteger(e, setUnlockThreshold, true)}
                  onBlur={() => onBlurPositiveInteger(unlockThreshold, setUnlockThreshold, 0)}
                  onKeyDown={preventInvalidKeys}
                  inputProps={{ min: 0, step: 1 }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">USDC</InputAdornment>,
                  }}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              {/* <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={translate('whitelist.reward.firstBatch')}
                  type="number"
                  value={initialSlot}
                  onChange={(e) => handlePositiveInteger(e, setInitialSlot)}
                  onBlur={() => onBlurPositiveInteger(initialSlot, setInitialSlot, 1)}
                  onKeyDown={preventInvalidKeys}
                  inputProps={{ min: 1, step: 1 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {translate('whitelist.personUnit')}
                      </InputAdornment>
                    ),
                  }}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid> */}
            </Grid>

            <PermissionGuard resource="whitelist-reward-config" action="update" fallback={null}>
              <Box sx={{ display: 'flex', gap: 2, mt: 4, mb: 3 }}>
                <Button variant="contained" onClick={handleWhiteListSave}>
                  {translate('whitelist.save')}
                </Button>
              </Box>
            </PermissionGuard>
          </Paper>

          {/* ========== PREDICTION TICKET TIERS ========== */}
          <Paper variant="outlined" sx={{ p: 2, mt: 3, bgcolor: 'background.default' }}>
            <Typography variant="h6" component="h2" gutterBottom fontWeight="medium">
              {translate('whitelist.reward.predictionTicketTiers')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {translate('whitelist.reward.predictionTicketDesc')}
            </Typography>

            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width="15%">#</TableCell>
                    <TableCell width="35%">
                      {translate('whitelist.reward.threshold')} (USDT)
                    </TableCell>
                    <TableCell width="35%">
                      {translate('whitelist.reward.unlockedQuantity')}
                    </TableCell>
                    <TableCell width="15%" align="center">
                      {translate('whitelist.reward.action')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tiers.map((tier, index) => (
                    <TableRow key={tier.position}>
                      <TableCell sx={{ py: 0.5, px: 1 }}>{index + 1}</TableCell>
                      <TableCell sx={{ py: 0.5, px: 1 }}>
                        <TextField
                          type="number"
                          value={tier.volumeUsdc}
                          onChange={(e) => {
                            const val = e.target.value.replace(/[^0-9]/g, '');
                            let num = parseInt(val, 10);
                            if (isNaN(num)) num = 1;
                            if (num < 1) num = 1;
                            updateTier(tier.position!, 'volumeUsdc', num);
                          }}
                          onKeyDown={preventInvalidKeys}
                          inputProps={{ min: 1, step: 1 }}
                          size="small"
                          fullWidth
                          InputProps={{
                            endAdornment: <InputAdornment position="end">USDT</InputAdornment>,
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ py: 0.5, px: 1 }}>
                        <TextField
                          type="number"
                          value={tier.tickets}
                          onChange={(e) => {
                            const val = e.target.value.replace(/[^0-9]/g, '');
                            let num = parseInt(val, 10);
                            if (isNaN(num)) num = 1;
                            if (num < 1) num = 1;
                            updateTier(tier.position!, 'tickets', num);
                          }}
                          onKeyDown={preventInvalidKeys}
                          inputProps={{ min: 1, step: 1 }}
                          size="small"
                          fullWidth
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                {translate('whitelist.reward.ticket')}
                              </InputAdornment>
                            ),
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          color="error"
                          onClick={() => handleDeleteTier(tier.position!)}
                          size="small"
                          sx={{ visibility: tiers.length <= 1 ? 'hidden' : 'visible' }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <PermissionGuard resource="whitelist-reward-config" action="update" fallback={null}>
              <Box
                sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              >
                <Button variant="text" startIcon={<AddIcon />} onClick={handleAddPredictTier}>
                  {translate('whitelist.reward.addTier')}
                </Button>
                <Button variant="contained" onClick={handleSavePredictTicketTier}>
                  {translate('whitelist.save')}
                </Button>
              </Box>
            </PermissionGuard>
          </Paper>

          {/* ========== LEADERBOARD TIER REWARD ========== */}
          <Paper variant="outlined" sx={{ p: 2, mt: 3, bgcolor: 'background.default' }}>
            <Typography variant="h6" component="h2" gutterBottom fontWeight="medium">
              {translate('whitelist.reward.leaderboardTierReward')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {translate('whitelist.reward.leaderboardTierRewardDesc')}
            </Typography>

            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width="10%">#</TableCell>
                    <TableCell width="35%">{translate('whitelist.reward.rankingRange')}</TableCell>
                    <TableCell width="40%">{translate('whitelist.reward.singleReward')}</TableCell>
                    <TableCell width="15%" align="center">
                      {translate('whitelist.reward.action')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rewardTiers.map((tier, index) => (
                    <TableRow key={tier.position}>
                      <TableCell sx={{ py: 0.5, px: 1 }}>{index + 1}</TableCell>
                      <TableCell sx={{ py: 0.5, px: 1 }}>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <TextField
                            type="number"
                            value={tier.rankFrom}
                            onChange={(e) => {
                              const val = e.target.value.replace(/[^0-9]/g, '');
                              let num = parseInt(val, 10);
                              if (isNaN(num)) num = 1;
                              if (num < 1) num = 1;
                              updateRewardTier(tier.position!, 'rankFrom', num);
                            }}
                            onKeyDown={preventInvalidKeys}
                            inputProps={{ min: 1, step: 1 }}
                            size="small"
                            placeholder="From"
                            sx={{ width: 100 }}
                            error={!!rewardTierErrors[tier.position!]?.rankFrom}
                            helperText={rewardTierErrors[tier.position!]?.rankFrom}
                          />
                          <Typography variant="body2">
                            {translate('whitelist.reward.to')}
                          </Typography>
                          <TextField
                            type="number"
                            value={tier.rankTo}
                            onChange={(e) => {
                              const val = e.target.value.replace(/[^0-9]/g, '');
                              let num = parseInt(val, 10);
                              if (isNaN(num)) num = 1;
                              if (num < 1) num = 1;
                              updateRewardTier(tier.position!, 'rankTo', num);
                            }}
                            onKeyDown={preventInvalidKeys}
                            inputProps={{ min: 1, step: 1 }}
                            size="small"
                            placeholder="To"
                            sx={{ width: 100 }}
                            error={!!rewardTierErrors[tier.position!]?.rankTo}
                            helperText={rewardTierErrors[tier.position!]?.rankTo}
                          />
                        </Box>
                      </TableCell>
                      <TableCell sx={{ py: 0.5, px: 1 }}>
                        <TextField
                          type="number"
                          value={tier.amountUsdc}
                          onChange={(e) => {
                            const val = e.target.value.replace(/[^0-9]/g, '');
                            let num = parseInt(val, 10);
                            if (isNaN(num)) num = 1;
                            if (num < 1) num = 1;
                            updateRewardTier(tier.position!, 'amountUsdc', String(num));
                          }}
                          onKeyDown={preventInvalidKeys}
                          inputProps={{ min: 1, step: 1 }}
                          size="small"
                          fullWidth
                          InputProps={{
                            endAdornment: <InputAdornment position="end">USDT</InputAdornment>,
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          color="error"
                          onClick={() => handleDeleteRewardTier(tier.position!)}
                          size="small"
                          sx={{ visibility: rewardTiers.length <= 1 ? 'hidden' : 'visible' }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <PermissionGuard resource="whitelist-reward-config" action="update" fallback={null}>
              <Box
                sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              >
                <Button variant="text" startIcon={<AddIcon />} onClick={handleAddRewardTier}>
                  {translate('whitelist.reward.addTier')}
                </Button>
                <Button variant="contained" onClick={handleSaveRewardTier}>
                  {translate('whitelist.save')}
                </Button>
              </Box>
            </PermissionGuard>
          </Paper>

          {/* ========== REFERRAL (INVITE) TIER REWARDS ========== */}
          <Paper variant="outlined" sx={{ p: 2, mt: 3, bgcolor: 'background.default' }}>
            <Typography variant="h6" component="h2" gutterBottom fontWeight="medium">
              {translate('whitelist.reward.referralTierRewards')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {translate('whitelist.reward.referralTierRewardsDesc')}
            </Typography>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6} sm={3}>
                <Typography variant="body2" color="text.secondary">
                  {translate('whitelist.referralPool.totalUsdc')}
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {referralPool?.totalUsdc ?? '-'} USDT
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="body2" color="text.secondary">
                  {translate('whitelist.referralPool.remainingUsdc')}
                </Typography>
                <Typography variant="body1" fontWeight="medium" color="success.main">
                  {referralPool?.remainingUsdc ?? '-'} USDT
                </Typography>
              </Grid>
            </Grid>





            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width="15%">#</TableCell>
                    <TableCell width="35%">
                      {translate('whitelist.reward.validInviteCount')}
                    </TableCell>
                    <TableCell width="35%">{translate('whitelist.reward.rewardAmount')}</TableCell>
                    <TableCell width="15%" align="center">
                      {translate('whitelist.reward.action')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {referralTiers.map((tier, index) => (
                    <TableRow key={tier.position}>
                      <TableCell sx={{ py: 0.5, px: 1 }}>{index + 1}</TableCell>
                      <TableCell sx={{ py: 0.5, px: 1 }}>
                        <TextField
                          type="number"
                          value={tier.minValid}
                          onChange={(e) => {
                            const val = e.target.value.replace(/[^0-9]/g, '');
                            let num = parseInt(val, 10);
                            if (isNaN(num)) num = 1;
                            if (num < 1) num = 1;
                            updateInviteTier(tier.position!, 'minValid', num);
                          }}
                          onKeyDown={preventInvalidKeys}
                          inputProps={{ min: 1, step: 1 }}
                          size="small"
                          fullWidth
                          InputProps={{
                            endAdornment: <InputAdornment position="end">人</InputAdornment>,
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ py: 0.5, px: 1 }}>
                        <TextField
                          type="number"
                          value={tier.amountUsdc}
                          onChange={(e) => {
                            const val = e.target.value.replace(/[^0-9]/g, '');
                            let num = parseInt(val, 10);
                            if (isNaN(num)) num = 1;
                            if (num < 1) num = 1;
                            updateInviteTier(tier.position!, 'amountUsdc', num);
                          }}
                          onKeyDown={preventInvalidKeys}
                          inputProps={{ min: 1, step: 1 }}
                          size="small"
                          fullWidth
                          InputProps={{
                            endAdornment: <InputAdornment position="end">USDT</InputAdornment>,
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          color="error"
                          onClick={() => handleDeleteInviteTier(tier.position!)}
                          size="small"
                          sx={{ visibility: referralTiers.length <= 1 ? 'hidden' : 'visible' }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <PermissionGuard resource="whitelist-reward-config" action="update" fallback={null}>
              <Box
                sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              >
                <Button variant="text" startIcon={<AddIcon />} onClick={handleAddInviteTier}>
                  {translate('whitelist.reward.addTier')}
                </Button>
                <Button variant="contained" onClick={handleSaveInviteTier}>
                  {translate('whitelist.save')}
                </Button>
              </Box>
            </PermissionGuard>
          </Paper>
        </CardContent>
      </Card>

      {/* ========== RESET DEFAULT SECTION ========== */}
      <Paper variant="outlined" sx={{ p: 3, mt: 3 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Box>
            <Typography variant="h6" component="h2" gutterBottom fontWeight="medium">
              {translate('whitelist.reward.reset')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {translate('whitelist.reward.resetDescription')}
            </Typography>
          </Box>
          <PermissionGuard resource="whitelist-reward-config" action="update" fallback={null}>
            <Button variant="outlined" color="error" onClick={handleResetToDefault}>
              {translate('whitelist.reward.reset')}
            </Button>
          </PermissionGuard>
        </Box>
      </Paper>
      </PermissionGuard>
    </DashboardContent>
  );
};

export default View;
